/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Dedicated full-screen 3D visualization for a model asset.
 *
 * Route: /ai/ml/models/:assetId/3d
 *
 * Shows the NeuralNetwork3D canvas at full viewport height with minimal
 * chrome — meant for focused inspection of model architecture.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import InsightsIcon from '@mui/icons-material/Insights';

import {
  modelLabAssetApi,
  modelLabTrainingJobApi,
  modelLabVersionApi,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import NeuralNetwork3D from './NeuralNetwork3D';
import { buildModel3DLayers, humanize } from './modelArchHelpers';
import PageBackButton from '../../../components/PageBackButton';

const ModelLab3DViewPage: React.FC = () => {
  const { assetId: assetIdParam } = useParams<{ assetId: string }>();
  const assetId = Number(assetIdParam);
  const navigate = useNavigate();
  const { user } = useAuth();

  const assetQuery = useQuery({
    queryKey: ['model-lab-asset', assetId],
    queryFn: () => modelLabAssetApi.get(assetId),
    enabled: !!assetId,
  });
  const versionsQuery = useQuery({
    queryKey: ['model-lab-asset-versions', assetId],
    queryFn: () => modelLabVersionApi.list({ model_asset: assetId, page_size: 50 }),
    enabled: !!assetId,
  });
  const jobsQuery = useQuery({
    queryKey: ['model-lab-asset-jobs', assetId],
    queryFn: () => modelLabTrainingJobApi.list({ model_asset: assetId, page_size: 50 }),
    enabled: !!assetId,
  });

  const asset = assetQuery.data;
  const versions = versionsQuery.data?.results || [];
  const jobs = jobsQuery.data?.results || [];

  // Pick the best version to introspect: prefer production, then staging, then highest version.
  const bestVersion = useMemo(() => {
    if (!versions.length) return null;
    const prod = versions.find((v) => v.stage === 'PRODUCTION');
    if (prod) return prod;
    const staging = versions.find((v) => v.stage === 'STAGING');
    if (staging) return staging;
    return [...versions].sort((a, b) => (b.version_number || 0) - (a.version_number || 0))[0];
  }, [versions]);

  const internalsQuery = useQuery({
    queryKey: ['model-lab-version-internals', bestVersion?.id],
    queryFn: () => modelLabVersionApi.internals(bestVersion!.id, { max_trees: 8, max_nodes: 32 }),
    enabled: !!bestVersion?.id,
    retry: false,
    staleTime: 60_000,
  });

  const { layers, algorithm, rawAlgorithm } = useMemo(
    () => buildModel3DLayers(asset, versions, jobs, internalsQuery.data),
    [asset, versions, jobs, internalsQuery.data],
  );

  if (!user?.is_staff) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">The 3D viewer is restricted to admin users.</Alert>
      </Box>
    );
  }

  const isLoading = assetQuery.isLoading || versionsQuery.isLoading || jobsQuery.isLoading;

  // Measure available height so the canvas fills the page container area.
  const [canvasHeight, setCanvasHeight] = useState<number>(600);
  useEffect(() => {
    const update = () => {
      // Subtract header + padding.
      setCanvasHeight(Math.max(400, window.innerHeight - 180));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const handleClose = () => {
    navigate(`/ai/ml/models/${assetId}/manager`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Top bar */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <PageBackButton label="Back to Manager" to={`/ai/ml/models/${assetId}/manager`} preferHistory={false} hideLabelOnMobile />
        <InsightsIcon color="secondary" />
        <Stack spacing={0} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }} noWrap>
            {asset?.name || `Asset #${assetId}`} — 3D View
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {algorithm
              ? `${humanize(algorithm)}${
                  rawAlgorithm && rawAlgorithm.toLowerCase() !== algorithm
                    ? ` (${rawAlgorithm})`
                    : ''
                }`
              : rawAlgorithm || 'unknown algorithm'}
            {' · '}
            {layers.length} layer{layers.length === 1 ? '' : 's'}
          </Typography>
        </Stack>
        <Chip
          size="small"
          label={asset?.problem_type || 'MODEL'}
          variant="outlined"
          sx={{ ml: 1 }}
        />
        {internalsQuery.isFetching && (
          <Chip size="small" label="loading internals…" color="info" variant="outlined" />
        )}
        {internalsQuery.data && (internalsQuery.data.trees?.length || internalsQuery.data.dl_layers?.length) ? (
          <Tooltip title="Real tree/weight data from trained model artifact">
            <Chip size="small" label="real weights" color="success" variant="filled" />
          </Tooltip>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button
          startIcon={<AutoAwesomeIcon />}
          size="small"
          variant="outlined"
          onClick={() => navigate(`/ai/ml/models/${assetId}/manager`)}
        >
          Open AI Manager
        </Button>
        <Tooltip title="Close 3D view">
          <IconButton onClick={handleClose} size="small" color="error">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {isLoading && <LinearProgress />}

      {/* Canvas fills remaining space */}
      <Box sx={{ flex: 1, minHeight: 0, p: 2 }}>
        {!isLoading && layers.length === 0 ? (
          <Alert severity="info">No layer information available for this model.</Alert>
        ) : (
          <NeuralNetwork3D layers={layers} height={canvasHeight} />
        )}
      </Box>
    </Box>
  );
};

export default ModelLab3DViewPage;
